import { Menu } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import help from '@/assets/icons/vn-icon-help.svg';
import onboarding from '@/assets/icons/vn-icon-onboarding.svg';
import { Permission } from '@/enums';
import { MODAL_KEYS } from '@/modals/keys';
import { useModalContext } from '@/modals/VNModalProvider';
import type { NavigationOptions } from '@/models/Ou';

import { LocalStorageService } from '../../../services/local-storage';
import { applicationDataSelector } from '../../../store/slices/application-data';
import { clientSettingsSelector, toggleSidebar } from '../../../store/slices/clientsettings';
import { fuelUserAccessSliceSelector } from '../../../store/slices/fuelUserAccess';
import { SaveAssetJobsSelector } from '../../../store/slices/saveAssetJobsState/SaveAssetJobsState';
import { cacheUtils } from '../../../utils/cacheUtils';
import ChatWidget from '../../ChatWidget';
import ExportAnalyticsProgressPopup from '../ExportAnalyticsProgressPopup';
import IngestUploadProgressPopup from '../IngestUploadProgressPopup';
import getSideBarlist from './getSideBarList';
import SidebarMenu from './SidebarMenu';

type Props = {
  preview?: boolean;
  availableOptions?: NavigationOptions;
};

const ViewNexaSideBar = ({ preview = false, availableOptions }: Props) => {
  const { isToggleSidebarOpen: sidebarOpen } = useSelector(clientSettingsSelector);
  const isToggleSidebarOpen = Boolean(preview) || sidebarOpen;
  const dispatch = useDispatch();
  const { ouData } = useSelector(applicationDataSelector);
  const { permissions } = useSelector(fuelUserAccessSliceSelector);
  const flags = useFlags();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeRoute, setActiveRoute] = useState('/assets/list');
  // const [openAssetIngestDialg, setOpenAssetIngestDialg] = useState(false);
  const { ingestInProgress } = useSelector(SaveAssetJobsSelector);
  const ingestUploadProgressRef = useRef();
  const exportAnalyticsProgressRef = useRef();
  const modal = useModalContext();

  const toggleDrawer = () => {
    if (preview) {
      return;
    }
    dispatch(toggleSidebar());
  };

  const filteredSidebarList = useMemo(() => {
    // TODO ouData should be always available
    if (!ouData) return [];

    const menuList = getSideBarlist(ouData, permissions, flags);

    return menuList
      .filter(Boolean)
      .filter(
        (item) =>
          (typeof item.featureFlagEnable === 'undefined' || item.featureFlagEnable) &&
          (!item.permission || permissions?.has(item.permission)) &&
          (!availableOptions || availableOptions?.[item.id])
      )
      .map((item) => {
        // if the item has children, filter them as well
        // and set the parent item to be expanded
        if (item.children && item.children.length > 0) {
          const children = item.children.filter(
            (child) =>
              (typeof child.featureFlagEnable === 'undefined' || child.featureFlagEnable) &&
              (!child.permission || permissions?.has(child.permission)) &&
              (!availableOptions || availableOptions?.[child.id])
          );
          return {
            ...item,
            children,
          };
        }
        return item;
      })
      .filter((item) => item.children?.length > 0 || typeof item.path === 'string'); // filter out items without children or path
  }, [ouData, permissions, flags, JSON.stringify(availableOptions)]);

  // console.log('filteredSidebarList', filteredSidebarList);

  // HACK fix for adding modal to upload assets
  const newAssetItem = filteredSidebarList.find((item) => item.path === '/assets/new');
  const enableUsePopupForIngest = flags['use-ingest-popup-for-upload-assets'];
  if (newAssetItem && enableUsePopupForIngest) {
    newAssetItem.onClick = (e) => {
      // modal.onOpen('Upload', 'xl', <VNAssetUpload />);
      modal.openByKey(MODAL_KEYS.UPLOAD_ASSET, { title: 'Upload', cssClassName: 'vn-asset-upload', size: 'sm' });
      e.preventDefault();
    };
  }

  const resolveActiveRoute = (_: unknown, to: string) => {
    console.info('viewnexa sidebar resolveActiveRoute', { to });

    // if the RELOAD_ON_NAVIGATION flag is set, then immediately reload
    const reloadOnNavigation = LocalStorageService.getItem('RELOAD_ON_NAVIGATION');
    if (reloadOnNavigation) {
      LocalStorageService.removeItem('RELOAD_ON_NAVIGATION');
      cacheUtils.doHardRefresh();
      return;
    }

    if (to === '/' || to === '/assets') {
      // set default pages for unresolved routes
      navigate('/assets/list');
    }
    if (to === '/channelmanager') {
      navigate('/channelmanager/list');
    }
    if (to === '/assets/new' && enableUsePopupForIngest) {
      navigate('/assets/list');
    }
    setActiveRoute(to);
  };
  const openIngestProgressPopup = () => {
    if (ingestUploadProgressRef && ingestUploadProgressRef.current) {
      ingestUploadProgressRef.current.open();
    }
  };

  useEffect(() => {
    // console.log('location changed', location);
    if (flags['enable-view-nexa-new-ui-design'] === true) {
      resolveActiveRoute(null, location.pathname);
    }
  }, [location]);

  useEffect(() => {
    // aalt 2025-08-27 #4363 prevent toggling the sidebar if we are in preview mode
    if (!preview) {
      dispatch(toggleSidebar());
    }
  }, []);

  return (
    <div className="sidebar-content">
      {/* Sidebar Drawer */}
      <Drawer
        variant="permanent"
        open={isToggleSidebarOpen}
        className={`sidebar ${isToggleSidebarOpen ? 'sidebar--expanded' : 'sidebar--collapsed'}`}
        style={{ zIndex: ingestInProgress ? 1301 : 200 }}
      >
        {/* Sidebar Header (Menu Icon + Title) */}
        <Box className="sidebar-header">
          {/* Menu Icon (Always Visible) */}
          <IconButton onClick={toggleDrawer} className="menu-icon">
            <Menu />
          </IconButton>

          {/* Title (Visible Only When Open) */}
          {/* {isToggleSidebarOpen && (
            <div className="layout-title">{title}</div>
          )} */}
        </Box>

        {/* Sidebar Items */}
        {/* Sidebar Items */}
        <SidebarMenu
          items={filteredSidebarList}
          isOpen={isToggleSidebarOpen}
          activeRoute={activeRoute}
          preview={preview}
          availableOptions={availableOptions}
        />
        <List className="sidebar-list">
          <Divider className="divider" />
          {ingestInProgress && (
            <ListItem>
              <Box sx={{ marginLeft: '5px', position: 'relative' }} onClick={openIngestProgressPopup}>
                <CircularProgress
                  size={25}
                  thickness={6}
                  sx={{
                    color: 'white',
                    zIndex: 1,
                  }}
                />
              </Box>
            </ListItem>
          )}

          {(!availableOptions || availableOptions.onboarding) && (
            <ListItem disablePadding className="list-item" title="Onboarding">
              <ListItemButton className="item-btn">
                <ListItemIcon className="menu-icon">
                  <img src={onboarding} alt="Onboarding" />
                </ListItemIcon>
                {isToggleSidebarOpen && <ListItemText className="item-text" primary="Onboarding" />}
              </ListItemButton>
            </ListItem>
          )}

          {permissions &&
            permissions.has(Permission.Chatbot) &&
            flags['view-nexa-support-chatbot'] &&
            (!availableOptions || availableOptions.viewNexaAssistant) && <ChatWidget newNavigation />}

          {(!availableOptions || availableOptions.help) && (
            <ListItem disablePadding className="list-item" title="Help site, articles, videos">
              <ListItemButton
                className="item-btn"
                onClick={() => window.open('https://viewnexa.zendesk.com/hc/en-us/', '_blank')}
              >
                <ListItemIcon className="menu-icon">
                  <img src={help} alt="Help" />
                </ListItemIcon>
                {isToggleSidebarOpen && <ListItemText className="item-text" primary="Help" />}
              </ListItemButton>
            </ListItem>
          )}
        </List>
        <Box className="logo-container">
          {/* Sidebar Logo (Placed at the Bottom) */}

          <IngestUploadProgressPopup ref={ingestUploadProgressRef} />
          <ExportAnalyticsProgressPopup ref={exportAnalyticsProgressRef} />
          {!isToggleSidebarOpen && <span className="viewnexa-logo" />}
          {isToggleSidebarOpen && flags['enable-view-nexa-new-ui-design'] && <span className="viewnexa-logo-full" />}
        </Box>
      </Drawer>
      {/* {openAssetIngestDialg && (
        <AssetDetailsModal open={openAssetIngestDialg} onClose={() => setOpenAssetIngestDialg(false)} isNew />
      )} */}
    </div>
  );
};

export default ViewNexaSideBar;
